import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { translate } from '@shakkerai/util-i18n';
import { Popover } from '@mantine/core';
import { Icon } from '@shakkerai/components';
import { isMainland } from '@shakkerai/pro-utils';
import { useTempState, setTempState } from '@starflow/app/store';
import { convert2CompressedUrl } from '@shakkerai/util-image';
import { applyModel, applyPresetModel } from '@starflow/app/modules/models/helpers';
import { mc } from '@shakkerai/report';
import { libVipIcon } from '@shakkerai/pro-commercial';
import crownIcon from './assets/crown_outlined.png';
import './style.scss';
import { useAsyncEffect } from 'ahooks';
import { request } from '@shakkerai/services';
import { presetCheckpoints } from '@shakkerai/pro-models';
import { switchGlobalMode } from '@starflow/app/modules/generator/helpers/generate';
import cs from 'classnames';

export const SuggestionBaseModelTipWrapper = React.memo(({ children }) => {
  const suggestionCheckpoint = useTempState('suggestionCheckpoint');

  const handleApplySuggestionBaseModel = () => {
    applyModel(suggestionCheckpoint);
    setTempState({ suggestionCheckpoint: null });

    mc('tool.model.suggestion.use', {
      type: 'checkpoint',
      model_version_uuid: suggestionCheckpoint.uuid,
    });
  };

  const handleClose = useCallback((event) => {
    if (event?.target?.closest('[data-role="apply-suggestion-base-model"]')) {
      return;
    }

    if (event?.target?.closest('[data-role="models-window"]')) {
      return;
    }

    setTempState({ suggestionCheckpoint: null });
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleClose, true);

    mc('tool.model.suggestion.show', {
      type: 'checkpoint',
      model_version_list: [suggestionCheckpoint?.uuid],
    });

    return () => {
      document.removeEventListener('click', handleClose, true);
    };
  }, [handleClose, suggestionCheckpoint]);

  if (!suggestionCheckpoint) return null;

  return (
    <Popover zIndex={99} opened={true} position="left-start" offset={22} radius={4} shadow="md" withinPortal={true}>
      <Popover.Target>{children}</Popover.Target>
      <Popover.Dropdown w={300} id="sd3-tip-container" className="w-[300px] p-0 bg-white border-none rounded-[1px]">
        <div className="flex items-center h-[44px] px-4 border-t-2 border-b border-b-[#efefef] border-t-primary">
          <h5 className="flex-1 text-black text-sm font-semibold">{translate('suggestion_base_model_title')}</h5>
          <button onClick={handleClose}>
            <Icon fontSize={16} className="icon-border_close-1 text-[#222]" />
          </button>
        </div>
        <div className="flex items-center p-4">
          <img
            className="w-[50px] h-[66px] bg-[#222] object-cover"
            src={convert2CompressedUrl(suggestionCheckpoint?.imageUrl, 100)}
            alt=""
          />
          <div className="flex flex-1 overflow-hidden flex-col ml-2.5 mr-3">
            <span className="text-sm text-[#2d2d2d] font-normal max-w-[200px] text-ellipsis overflow-hidden whitespace-nowrap">
              {suggestionCheckpoint?.name || 'Name'}
            </span>

            <div className="flex gap-2 items-center">
              <span className="text-xs text-[#8c8c8c] font-normal max-w-[200px]">
                {suggestionCheckpoint?.baseType || 'baseType'}
              </span>
              {suggestionCheckpoint?.vipUsed === 1 && (
                <>
                  <div className="bg-[#EFEFEF] w-[1px] h-[10px]"></div>
                  <img src={isMainland ? libVipIcon : crownIcon} className="w-[13px]" alt="" />
                </>
              )}
            </div>
          </div>
          <button
            data-role="apply-suggestion-base-model"
            onClick={handleApplySuggestionBaseModel}
            className="h-6 px-2 text-[#2d2d2d] text-xs font-normal bg-primary/10 border border-primary rounded-sm"
          >
            {translate('model_use')}
          </button>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
});

export const NewlyFavoritedModelTipWrapper = React.memo(({ children }) => {
  const newlyFavoritedModel = useTempState('newlyFavoritedModel');
  const openedSubWindow = useTempState('exclusiveWindow');

  const handleApplyFavoritedBaseModel = () => {
    switchGlobalMode('pro');
    applyModel(newlyFavoritedModel);
    setTempState({ newlyFavoritedModel: null });
  };

  const handleClose = useCallback((event) => {
    const openedSubWindow = getTempState('exclusiveWindow');

    if (openedSubWindow) return;

    if (event?.target?.closest('[data-role="apply-favorited-base-model"]')) {
      return;
    }
    setTempState({ newlyFavoritedModel: null });
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleClose, true);
    return () => {
      document.removeEventListener('click', handleClose, true);
    };
  }, [handleClose]);

  if (!newlyFavoritedModel || openedSubWindow) return children;

  return (
    <Popover zIndex={99} opened={true} position="left-start" offset={22} radius={4} shadow="md" withinPortal={true}>
      <Popover.Target>{children}</Popover.Target>
      <Popover.Dropdown w={300} id="sd3-tip-container" className="w-[300px] p-0 bg-white border-none rounded-[1px]">
        <div className="flex items-center h-[44px] px-4 border-t-2 border-b border-b-[#efefef] border-t-primary">
          <h5 className="flex-1 text-black text-sm font-semibold">{translate('newly_favorited_appy_tip')}</h5>
          <button onClick={handleClose}>
            <Icon fontSize={16} className="icon-border_close-1 text-[#222]" />
          </button>
        </div>
        <div className="flex items-center p-4">
          <img
            className="w-[50px] h-[66px] bg-[#222] object-cover"
            src={convert2CompressedUrl(newlyFavoritedModel?.imageUrl, 100)}
            alt=""
          />
          <div className="flex flex-1 overflow-hidden flex-col ml-2.5 mr-3">
            <span className="text-sm text-[#2d2d2d] font-normal max-w-[200px] text-ellipsis overflow-hidden whitespace-nowrap">
              {newlyFavoritedModel?.name || '11'}
            </span>
            <div className="flex gap-2 items-center">
              <span className="text-xs text-[#8c8c8c] font-normal max-w-[200px]">
                {newlyFavoritedModel?.baseType || 22}
              </span>
              {newlyFavoritedModel?.vipUsed === 1 && (
                <>
                  <div className="bg-[#EFEFEF] w-[1px] h-[10px]"></div>
                  <img src={isMainland ? libVipIcon : crownIcon} className="w-[13px]" alt="" />
                </>
              )}
            </div>
          </div>
          <button
            data-role="apply-favorited-base-model"
            onClick={handleApplyFavoritedBaseModel}
            className="h-6 px-2 text-[#2d2d2d] text-xs font-normal bg-primary/10 border border-primary rounded-sm"
          >
            {translate('model_use')}
          </button>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
});

export const CommonTipWrapper = React.memo(
  ({
    position = 'top-end',
    arrowOffset = 0,
    title,
    subTitle,
    buttonText,
    onButtonClick,
    closeOnBlur,
    children,
    onClose,
  }) => {
    useEffect(() => {
      closeOnBlur && document.addEventListener('click', onClose);
      return () => {
        closeOnBlur && document.removeEventListener('click', onClose);
      };
    }, [closeOnBlur, onClose]);

    return (
      <Popover opened={true} radius={4} position={position} arrowOffset={arrowOffset} withArrow shadow="md">
        <Popover.Target>{children}</Popover.Target>
        <Popover.Dropdown id="common-tip-container" className="p-2 bg-white border-none rounded-[1px]">
          {title && <h5 className="text-sm text-[#222] font-bold">{title}</h5>}
          {subTitle && <p className="text-xs text-[#222]">{subTitle}</p>}
          {buttonText && (
            <button
              onClick={onButtonClick}
              className="h-6 mt-2 px-2 text-black text-xs font-normal bg-white rounded-[px]"
            >
              {buttonText}
            </button>
          )}
        </Popover.Dropdown>
      </Popover>
    );
  },
);

export const ModelAddedTip = React.memo(({ position = 'left-center', children, onClose }) => {
  useEffect(() => {
    document.addEventListener('click', onClose);
    return () => {
      document.removeEventListener('click', onClose);
    };
  }, [onClose]);

  return (
    <Popover opened={true} position={position} radius={4} withArrow shadow="md" withinPortal={true}>
      <Popover.Target>{children}</Popover.Target>
      <Popover.Dropdown id="sd3-tip-container" className="p-2 bg-white border-none rounded-[1px]">
        <p className="text-xs text-[#222]">{translate('model_added_tip')}</p>
      </Popover.Dropdown>
    </Popover>
  );
});

export const NormalGide = React.memo(({ children, currId }) => {
  const [baseModelRunCount, setBaseModelRunCount] = useState();
  const using = useMemo(() => currId === presetCheckpoints[0].id, [currId]);
  useEffect(() => {
    setTempState({
      showLoraAddedTip: false,
      showCheckpointAddedTip: false,
      showSuggestionBaseModelTip: false,
      showNewlyFavoritedModelTip: false,
      showNewlyFavoritedLoraTip: false,
    });
  }, []);
  useAsyncEffect(async () => {
    const uuid = presetCheckpoints[0].versionDetail.uuid;
    try {
      const result = await request(`/api/www/img/tool/style/model/baseDetail/${uuid}`);
      if (result.code === 0) {
        setBaseModelRunCount(result.data.runCount);
      }
    } catch (err) {
      console.log('api error', err);
    }
  }, []);
  const chooseBaseModel = () => {
    if (using) return;
    applyPresetModel(presetCheckpoints[0].id);
  };
  return (
    <Popover
      opened={true}
      width={520}
      position="left-start"
      style={{ boxShadow: '0px 2px 24px 0px rgba(0, 0, 0, 0.10)' }}
      arrowSize={0}
      radius={2}
      offset={17}
      withArrow
      shadow="md"
      withinPortal={true}
    >
      <Popover.Target>{children}</Popover.Target>
      <Popover.Dropdown id="sd3-tip-container" className="p-0 bg-white border-none rounded-[2px]">
        <header className="h-11 flex items-center justify-between">
          <h5 className="ml-4 text-sm text-[#2D2D2D] font-semibold ">{translate('model_ckp')}</h5>
          <Icon
            className="shk-prompt-lora-guanbi mr-4 close-icon-gide-tips cursor-pointer"
            onClick={() => setTempState({ showNormalCkptGide: false })}
            color="rgba(34, 34, 34, 1)"
            fontSize={12}
          />
        </header>
        <hr />
        <div className={cs('p-4 normal-gide-content', { 'cursor-pointer': !using })} onClick={() => chooseBaseModel()}>
          <div className={cs('w-[488px] gid-content flex', { 'gid-content-none': !using })}>
            <div className="w-[120px] h-[152px] relative">
              <img
                src={convert2CompressedUrl(presetCheckpoints[0].imageVertical, 470)}
                className="w-full h-full"
                alt=""
              />
              {baseModelRunCount > 0 ? (
                <div className="h-5 w-full flex justify-center absolute bottom-[4px] text-[#fff] text-xs font-normal not-italic">
                  <Icon className="shk-danchuang-yunhangliang mr-1 pt-[1px]" fontSize={12} color="#fff" />
                  <span>
                    {baseModelRunCount >= 1000 ? `${(baseModelRunCount / 1000).toFixed(1)}k` : baseModelRunCount}
                  </span>
                </div>
              ) : null}
            </div>
            <div className=" flex-1 relative overflow-hidden">
              {using ? (
                <span className="text-[#2C2C2C] gide-icon-bg text-xs not-italic font-medium flex justify-center items-center pl-4 pr-3 h-[26px] absolute right-0 top-0">
                  <div className="absolute top-0 left-0 bg-primary w-20 h-[26px] rounded-bl-lg skew-x-12" />
                  <span className="relative z-[2]">{translate('style_using')}</span>
                </span>
              ) : null}
              <div className="h-12 flex items-center px-4">
                <span className="gide-icon block">{translate('preset_basemodle_update')}</span>
                <h5 className="text-[#2D2D2D] text-sm font-medium not-italic">
                  {translate('preset_basemodle_shakker')}
                </h5>
              </div>
              <ul>
                <li className="gide-single-line">
                  <i />
                  {translate('preset_basemodle_desc1')}
                </li>
                <li className="gide-single-line">
                  <i />
                  {translate('preset_basemodle_desc2')}
                </li>
                <li className="gide-single-line">
                  <i />
                  {translate('preset_basemodle_desc3')}
                </li>
              </ul>
            </div>
          </div>
          <span className="flex justify-center pt-4 text-[#7F7F7F] text-xs not-italic font-normal">
            {translate('preset_basemodle_promode')}
          </span>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
});
